'use client'

import clsx from 'clsx'
import { ChangeEvent, Fragment, useState } from 'react'
import { Image, useTranslations } from '@/lib/next-shim'
import { t as linguiT } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { Dialog, Menu, Transition } from '@headlessui/react'
import { DocumentTextIcon, FolderOpenIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { CheckIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid'
import { UploadCloud } from 'react-feather'

import IconButton from '@mui/material/IconButton'

import { GetPatientFilesDocument, useDeleteFilesMutation, useGetPatientFilesQuery } from '@/core/graphql/hooks'
import { useUploadFile } from '@/shared/model/useUploadFile'

import { DeleteConfirmationDialog } from '@/shared/ui/dialog/DeleteConfirmationDialog'
import { objectTypes } from '@/core/api/modules/document/types'

export type DocumentsProps = {
  consultationId: string
}

const isImageFile = (filename: string) => /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/i.test(filename)

export function Documents({ consultationId }: DocumentsProps) {
  const t = useTranslations()

  const { data, refetch } = useGetPatientFilesQuery()

  const [deleteFiles, { loading: isDeleting }] = useDeleteFilesMutation()
  const [uploadFile, isUploadingFile] = useUploadFile()

  // Only show files attached to THIS consultation — not documents from the patient's other or
  // past consultations.
  const consultationDocuments = (data?.myInfos.consultations || [])
    .filter((consultation) => consultation._id === consultationId)
    .flatMap((consultation) =>
      (consultation.files || []).map((file) => ({
        filename: file.filename,
        key: file.key,
        objectType: 'consultation',
        url: file.url || '',
        objectId: consultation._id,
      }))
    )
  const patientDocuments = data?.myInfos._id
    ? (data?.myInfos.files || []).map((file) => ({
        filename: file.filename,
        key: file.key,
        objectType: 'patient',
        url: file.url || '',
        objectId: data.myInfos._id,
      }))
    : []
  const documents = patientDocuments.concat(consultationDocuments)

  const [open, setOpen] = useState(false)
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)
  const [selectedDocuments, setSelectedDocuments] = useState<{ [k: string]: (typeof documents)[number] }>({})

  const handleClose = () => {
    setOpen(false)
    setSelectedDocuments({})
  }

  const handleDeleteFiles = () => {
    if (!Object.keys(selectedDocuments).length) return

    const filesToDelete = Object.values(selectedDocuments)
      .filter((doc) => doc.objectId) // Filter out any documents without objectId
      .map((document) => ({
        filename: document.filename,
        key: document.key,
        objectType: document.objectType,
        objectId: document.objectId,
      }))

    if (!filesToDelete.length) return

    deleteFiles({
      variables: {
        payload: {
          files: filesToDelete,
        },
      },
      refetchQueries: [{ query: GetPatientFilesDocument }],
      onCompleted: () => {
        setSelectedDocuments({})
        setOpenDeleteConfirmationDialog(false)
      },
    })
  }

  const handleUploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      await uploadFile(file, consultationId, objectTypes[0])
      await refetch()
    }
    // Reset so selecting the same file again still triggers onChange.
    event.target.value = ''
  }

  const toggleSelectDocument = (document: (typeof documents)[number]) => {
    setSelectedDocuments((prevSelectedDocuments) => {
      let nextSelectedDocuments: typeof selectedDocuments = {}
      if (prevSelectedDocuments[document.key]) {
        for (let key in prevSelectedDocuments) {
          if (key !== document.key) nextSelectedDocuments[key] = prevSelectedDocuments[key]
        }
      } else {
        nextSelectedDocuments = { ...prevSelectedDocuments, [document.key]: document }
      }
      return nextSelectedDocuments
    })
  }

  return (
    <>
      <IconButton onClick={() => setOpen(true)} size="large" aria-label={t('meetingControls.documents')}>
        <FolderOpenIcon className="h-6 w-6 inline-block select-none text-white" />
      </IconButton>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={handleClose}>
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md mt-20 mb-40 md:mb-24 pr-5 min-w-0 overflow-hidden">
                    <div className="flex h-full flex-col bg-white rounded-md shadow-xl">
                      <div className="border-b border-gray-200 p-6">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">{t('dialog.Documents.title')}</Dialog.Title>
                          <div className="ml-3 flex items-center gap-3 flex-wrap">
                            <label
                              className={clsx(
                                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white cursor-pointer whitespace-nowrap',
                                isUploadingFile ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary/90'
                              )}
                            >
                              <input
                                type="file"
                                aria-label={t('dialog.Documents.upload')}
                                className="hidden"
                                disabled={isUploadingFile}
                                onChange={handleUploadFile}
                              />
                              <UploadCloud className="h-4 w-4" aria-hidden="true" />
                              {isUploadingFile ? t('dialog.Documents.uploading') : t('dialog.Documents.upload')}
                            </label>
                            {!!Object.keys(selectedDocuments).length && (
                              <>
                                <button
                                  type="button"
                                  aria-label={linguiT`Delete`}
                                  className="flex text-gray-400 hover:text-gray-500"
                                  onClick={() => setOpenDeleteConfirmationDialog(true)}
                                >
                                  <TrashIcon className="h-6 w-6" aria-hidden="true" />
                                </button>
                                <DeleteConfirmationDialog
                                  description={t('dialog.DeleteFilesConfirmation.description')}
                                  isDeleting={isDeleting}
                                  onDelete={handleDeleteFiles}
                                  onClose={() => setOpenDeleteConfirmationDialog(false)}
                                  open={openDeleteConfirmationDialog}
                                  title={t('dialog.DeleteFilesConfirmation.title', { documents: Object.keys(selectedDocuments).length })}
                                />
                              </>
                            )}
                            <button type="button" aria-label={linguiT`Close`} className="flex text-gray-400 hover:text-gray-500" onClick={handleClose}>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <ul role="list" className="flex-1 divide-y divide-gray-200 overflow-y-auto no-scrollbar">
                        {documents.map((document) => (
                          <li key={document.key}>
                            <div className="group relative flex items-center px-5 py-6">
                              <div className="block w-full flex-1 p-1">
                                <div className="absolute inset-0 group-hover:bg-gray-50" aria-hidden="true" />
                                <div className="relative flex min-w-0 flex-1 items-center">
                                  {!selectedDocuments[document.key] ? (
                                    isImageFile(document.filename) && document.url ? (
                                      <Image
                                        className="h-10 w-10 rounded-full cursor-pointer object-cover"
                                        onClick={() => toggleSelectDocument(document)}
                                        src={document.url}
                                        alt=""
                                      />
                                    ) : (
                                      <span
                                        className="h-10 w-10 rounded-full bg-gray-100 cursor-pointer flex items-center justify-center"
                                        onClick={() => toggleSelectDocument(document)}
                                        aria-hidden="true"
                                      >
                                        <DocumentTextIcon className="h-6 w-6 text-gray-500" />
                                      </span>
                                    )
                                  ) : (
                                    <span
                                      className="h-10 w-10 bg-primary cursor-pointer flex items-center justify-center rounded-full"
                                      onClick={() => toggleSelectDocument(document)}
                                    >
                                      <CheckIcon className="h-6 w-6 text-white" />
                                    </span>
                                  )}
                                  <div className="flex-1 ml-4 truncate">
                                    <p className="truncate text-sm font-medium text-gray-900">{document.filename}</p>
                                    {/* <p className="truncate text-sm text-gray-500">{'@' + person.handle}</p> */}
                                  </div>
                                  <Menu as="div" className="relative ml-2 inline-block flex-shrink-0 text-left">
                                    <Menu.Button className="group relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                                      <span className="sr-only">
                                        <Trans>Open options menu</Trans>
                                      </span>
                                      <span className="flex h-full w-full items-center justify-center rounded-full">
                                        <EllipsisVerticalIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                                      </span>
                                    </Menu.Button>
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform opacity-0 scale-95"
                                      enterTo="transform opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform opacity-100 scale-100"
                                      leaveTo="transform opacity-0 scale-95"
                                    >
                                      <Menu.Items className="absolute right-9 top-0 z-10 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div className="py-1">
                                          <Menu.Item>
                                            {({ active }) => (
                                              <a
                                                href={document.url || '#'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={clsx(
                                                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                  !document.url && 'pointer-events-none opacity-50',
                                                  'block px-4 py-2 text-sm'
                                                )}
                                              >
                                                {t('dialog.Documents.preview')}
                                              </a>
                                            )}
                                          </Menu.Item>
                                          <Menu.Item>
                                            {({ active }) => (
                                              <a
                                                href={document.url || '#'}
                                                download={document.filename}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={clsx(
                                                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                  !document.url && 'pointer-events-none opacity-50',
                                                  'block px-4 py-2 text-sm'
                                                )}
                                              >
                                                {t('dialog.Documents.download')}
                                              </a>
                                            )}
                                          </Menu.Item>
                                        </div>
                                      </Menu.Items>
                                    </Transition>
                                  </Menu>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}
